import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Avatar, Typography, Spin, message, Divider, Tag, Row, Col, Button, Modal, Form, Input, TimePicker, Select, Checkbox } from 'antd';
import { 
  MailOutlined, 
  IdcardOutlined, 
  ClockCircleOutlined, 
  TeamOutlined,
  CalendarOutlined,
  CoffeeOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  EditOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;

const UserData = () => {

  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {

    const fetchData = async () => {

      try {

        const authToken = localStorage.getItem('authToken');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/user_data/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (res.data.success && res.data.data) {

          setUser(res.data.data);

        } else {

          throw new Error('Estructura de datos inesperada');

        }
        
        setLoading(false);

      } catch (err) {

        messageApi.error(err.response?.data?.message || err.message || 'Error al obtener datos');
        setLoading(false);

      }

    };

    fetchData();

  }, [id, messageApi]);

  const formatTime = (time) => {

     if (!time) return 'No definido';
  
    try {

      if (dayjs.isDayjs(time)) {

        return time.format('HH:mm');

      }
      
      if (typeof time === 'string' && time.match(/^\d{2}:\d{2}:\d{2}$/)) {

        return dayjs(time, 'HH:mm:ss').format('HH:mm');

      }
      
      return dayjs(time).format('HH:mm');

    } catch (e) {

      console.error('Error formateando hora:', time, e);
      return time;

    }

  };

  const parseTimeString = (time) => {

    if (!time) return null;

    if (dayjs.isDayjs(time)) return time.format('HH:mm:ss');

    if (typeof time === 'string') {

      if (time.match(/^\d{2}:\d{2}:\d{2}$/)) return time;

      if (time.match(/^\d{2}:\d{2}$/)) return `${time}:00`;

    }

    return null;

  };

  const safeWorkDays = (workDays) => {

    try {

      if (!workDays) return [];

      if (Array.isArray(workDays)) return workDays;

      if (typeof workDays === 'string') return workDays.split(',').filter(Boolean);

      return [];

    } catch (e) {

      return [];

    }

  };

  if (loading) {

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Cargando perfil..." />
      </div>
    );
    
  }

  if (!user) {

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Text type="danger">No se pudieron cargar los datos del usuario</Text>
      </div>
    );

  }

  return (

    <div style={{ 
      maxWidth: 1200, 
      margin: '0 auto', 
      padding: '16px',
      minHeight: '70vh',
      backgroundColor: '#353435',
      position: 'relative'
    }}>

      {contextHolder}

      <Button 
        type="text" 
        icon={<ArrowLeftOutlined style={{ color: '#f49b33', fontSize: '20px' }} />}
        onClick={() => navigate(-1)}
        style={{
          position: 'absolute',
          left: '16px',
          top: '16px',
          zIndex: 1,
          padding: '4px',
          height: 'auto'
        }}
      />

      <Button 
        type="text" 
        icon={<EditOutlined style={{ color: '#f49b33', fontSize: '20px' }} />}
        onClick={() => setIsModalOpen(true)}
        style={{
          position: 'absolute',
          right: '16px',
          top: '16px',
          zIndex: 1,
          padding: '4px',
          height: 'auto'
        }}
      />

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '70vh'
      }}>
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} md={8} style={{ height: '100%' }}>
            <Card 
              style={{ 
                height: '100%',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                borderRadius: '8px',
                background: '#2a2a2a'
              }}
              styles={{
                body: {
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }
              }}
            >
              <Avatar 
                size={100} 
                style={{ 
                  backgroundColor: '#f49b33', 
                  color: '#353435',
                  fontSize: '40px',
                  marginBottom: '12px'
                }}
                icon={<UserOutlined />}
              >
                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
              </Avatar>
              
              <Title level={4} style={{ marginBottom: '4px', color: '#f49b33' }}>
                {user.firstName} {user.lastName}
              </Title>
              
              <Tag 
                color={user.role === 'admin' ? '#f49b33' : user.role === 'moderator' ? '#2db7f5' : '#87d068'}
                style={{ 
                  borderRadius: '8px',
                  padding: '2px 8px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: user.role === 'admin' ? '#353435' : '#fff'
                }}
              >
                {user.role === 'admin' ? 'Administrador' : 
                user.role === 'moderator' ? 'Moderador' : 'Miembro'}
              </Tag>
              
              <Divider style={{ margin: '12px 0', backgroundColor: '#444' }} />
              
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <MailOutlined style={{ color: '#f49b33', fontSize: '18px', marginRight: '10px' }} />
                  <div>
                    <Text strong style={{ display: 'block', color: '#f49b33' }}>Correo electrónico</Text>
                    <Text style={{ color: '#ddd' }}>{user.email || 'No especificado'}</Text>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <IdcardOutlined style={{ color: '#f49b33', fontSize: '18px', marginRight: '10px' }} />
                  <div>
                    <Text strong style={{ display: 'block', color: '#f49b33' }}>Nombre de usuario</Text>
                    <Text style={{ color: '#ddd' }}>{user.username || 'No especificado'}</Text>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <TeamOutlined style={{ color: '#f49b33', fontSize: '18px', marginRight: '10px' }} />
                  <div>
                    <Text strong style={{ display: 'block', color: '#f49b33' }}>Área</Text>
                    <Text style={{ color: '#ddd' }}>{user.area}</Text>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} md={16} style={{ height: '100%' }}>
            <Card 
              title={<span style={{ color: '#f49b33' }}>Horario y Disponibilidad</span>} 
              style={{
                height: '100%',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                borderRadius: '8px',
                background: '#2a2a2a'
              }}
              styles={{
                header: {
                  borderBottom: '1px solid #444',
                  fontWeight: '500',
                  fontSize: '16px'
                },
                body: {
                  padding: '16px'
                }
              }}
            >
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12}>
                  <Card 
                    size="small" 
                    style={{ 
                      backgroundColor: '#353435',
                      border: '1px solid #f49b33',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ClockCircleOutlined style={{ color: '#f49b33', fontSize: '18px', marginRight: '10px' }} />
                      <div>
                        <Text strong style={{ display: 'block', color: '#f49b33' }}>Horario laboral</Text>
                        <Text style={{ color: '#ddd' }}>Entrada: {formatTime(user.entryTime)}</Text><br />
                        <Text style={{ color: '#ddd' }}>Salida: {formatTime(user.exitTime)}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Card 
                    size="small" 
                    style={{ 
                      backgroundColor: '#353435',
                      border: '1px solid #52c41a',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarOutlined style={{ color: '#52c41a', fontSize: '18px', marginRight: '10px' }} />
                      <div>
                        <Text strong style={{ display: 'block', color: '#52c41a' }}>Días de trabajo</Text>
                        <Text style={{ color: '#ddd' }}>
                          {safeWorkDays(user.workDays).length > 0
                            ? safeWorkDays(user.workDays)
                                .map(day => ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'][parseInt(day) - 1] || day)
                                .join(', ')
                            : 'No definidos'}
                        </Text>
                      </div>
                    </div>
                  </Card>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Card 
                    size="small" 
                    style={{ 
                      backgroundColor: '#353435',
                      border: '1px solid #1890ff',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CoffeeOutlined style={{ color: '#1890ff', fontSize: '18px', marginRight: '10px' }} />
                      <div>
                        <Text strong style={{ display: 'block', color: '#1890ff' }}>Tiempo de almuerzo</Text>
                        <Text style={{ color: '#ddd' }}>Inicio: {formatTime(user.lunchTimeStart)}</Text><br />
                        <Text style={{ color: '#ddd' }}>Fin: {formatTime(user.lunchTimeEnd)}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Card 
                    size="small" 
                    style={{ 
                      backgroundColor: '#353435',
                      border: '1px solid #eb2f96',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CoffeeOutlined style={{ color: '#eb2f96', fontSize: '18px', marginRight: '10px' }} />
                      <div>
                        <Text strong style={{ display: 'block', color: '#eb2f96' }}>Tiempo de descanso</Text>
                        <Text style={{ color: '#ddd' }}>Inicio: {formatTime(user.breakTimeStart)}</Text><br />
                        <Text style={{ color: '#ddd' }}>Fin: {formatTime(user.breakTimeEnd)}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
              
              <Divider style={{ margin: '16px 0', backgroundColor: '#444' }} />
            
            </Card>
          </Col>
        </Row>

        {user && (
          <Modal
            title={<span style={{ color: '#f49b33' }}>Editar Perfil</span>}
            open={isModalOpen}
            onCancel={() => {
              form.resetFields();
              setIsModalOpen(false);
            }}
            onOk={() => {

              form.validateFields().then(async (values) => {

                try {

                  const authToken = localStorage.getItem('authToken');

                  await axios.put(`${process.env.REACT_APP_API_URL}/user_data/${id}`, {
                    ...values,
                    entryTime: parseTimeString(values.entryTime),
                    exitTime: parseTimeString(values.exitTime),
                    lunchTimeStart: parseTimeString(values.lunchTimeStart),
                    lunchTimeEnd: parseTimeString(values.lunchTimeEnd),
                    breakTimeStart: parseTimeString(values.breakTimeStart),
                    breakTimeEnd: parseTimeString(values.breakTimeEnd),
                    workDays: values.workDays?.join(',')
                  }, {
                    headers: { Authorization: `Bearer ${authToken}` }
                  });

                  message.success('Perfil actualizado');
                  setIsModalOpen(false);
                  setUser(prev => ({ ...prev, ...values }));

                } catch (err) {

                  messageApi.error(err.response?.data?.error || 'Error al actualizar');

                }

              })
              .catch(() => {

                messageApi.warning('Por favor completa todos los campos requeridos');

              });

            }}
            
            okText="Guardar"
            cancelText="Cancelar"
            centered
            destroyOnClose
            width={800}
            styles={{
              header: {
                backgroundColor: '#353435',
                borderBottom: '1px solid #f49b33',
                padding: '16px 24px'
              },
              content: {
                backgroundColor: '#353435',
                color: '#fff',
                padding: '24px'
              },
              footer: {
                backgroundColor: '#353435',
                borderTop: '1px solid #f49b33',
                padding: '16px 24px'
              }
            }}
            okButtonProps={{
              style: {
                backgroundColor: '#f49b33',
                borderColor: '#f49b33',
                color: '#353435',
                fontWeight: 'bold',
                height: '40px',
                padding: '0 24px',
                marginLeft: '16px'
              }
            }}
            cancelButtonProps={{
              style: {
                backgroundColor: 'transparent',
                borderColor: '#f49b33',
                color: '#f49b33',
                height: '40px',
                padding: '0 24px'
              }
            }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                username: user.username,
                areaId: user.areaId,
                workDays: safeWorkDays(user.workDays),
                entryTime: user.entryTime ? dayjs(user.entryTime, 'HH:mm:ss') : null,
                exitTime: user.exitTime ? dayjs(user.exitTime, 'HH:mm:ss') : null,
                lunchTimeStart: user.lunchTimeStart ? dayjs(user.lunchTimeStart, 'HH:mm:ss') : null,
                lunchTimeEnd: user.lunchTimeEnd ? dayjs(user.lunchTimeEnd, 'HH:mm:ss') : null,
                breakTimeStart: user.breakTimeStart ? dayjs(user.breakTimeStart, 'HH:mm:ss') : null,
                breakTimeEnd: user.breakTimeEnd ? dayjs(user.breakTimeEnd, 'HH:mm:ss') : null
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item 
                    name="firstName" 
                    label={<span style={{ color: '#f49b33' }}>Nombre</span>} 
                    rules={[{ required: true }]}
                  >
                    <Input 
                      style={{ 
                        backgroundColor: '#2a2a2a', 
                        color: '#fff',
                        borderColor: '#555'
                      }} 
                    />
                  </Form.Item>

                  <Form.Item 
                    name="lastName" 
                    label={<span style={{ color: '#f49b33' }}>Apellido</span>} 
                    rules={[{ required: true }]}
                  >
                    <Input 
                      style={{ 
                        backgroundColor: '#2a2a2a', 
                        color: '#fff',
                        borderColor: '#555'
                      }} 
                    />
                  </Form.Item>

                  <Form.Item 
                    name="email" 
                    label={<span style={{ color: '#f49b33' }}>Correo electrónico</span>} 
                    rules={[{ type: 'email' }]}
                  >
                    <Input 
                      style={{ 
                        backgroundColor: '#2a2a2a', 
                        color: '#fff',
                        borderColor: '#555'
                      }} 
                    />
                  </Form.Item>

                  <Form.Item 
                    name="username" 
                    label={<span style={{ color: '#f49b33' }}>Nombre de usuario</span>}
                    rules={[{ required: true }]}
                  >
                    <Input 
                      style={{ 
                        backgroundColor: '#2a2a2a', 
                        color: '#fff',
                        borderColor: '#555'
                      }} 
                    />
                  </Form.Item>

                  <Form.Item 
                    name="areaId" 
                    label={<span style={{ color: '#f49b33' }}>Área</span>}
                  >
                    <Select
                      style={{ 
                        backgroundColor: '#2a2a2a',
                        color: '#fff'
                      }}
                      dropdownStyle={{
                        backgroundColor: '#353435'
                      }}
                    >
                      <Select.Option value={1} style={{ color: '#fff', backgroundColor: '#353435' }}>It</Select.Option>
                      <Select.Option value={2} style={{ color: '#fff', backgroundColor: '#353435' }}>Caller</Select.Option>
                      <Select.Option value={3} style={{ color: '#fff', backgroundColor: '#353435' }}>Quoter</Select.Option>
                      <Select.Option value={4} style={{ color: '#fff', backgroundColor: '#353435' }}>Team Leader</Select.Option>
                      <Select.Option value={5} style={{ color: '#fff', backgroundColor: '#353435' }}>Orders</Select.Option>
                      <Select.Option value={6} style={{ color: '#fff', backgroundColor: '#353435' }}>Supervisor</Select.Option>
                      <Select.Option value={7} style={{ color: '#fff', backgroundColor: '#353435' }}>Designer</Select.Option>
                      <Select.Option value={8} style={{ color: '#fff', backgroundColor: '#353435' }}>Support</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        name="entryTime" 
                        label={<span style={{ color: '#f49b33' }}>Hora de entrada</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="exitTime" 
                        label={<span style={{ color: '#f49b33' }}>Hora de salida</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        name="lunchTimeStart" 
                        label={<span style={{ color: '#f49b33' }}>Inicio almuerzo</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="lunchTimeEnd" 
                        label={<span style={{ color: '#f49b33' }}>Fin almuerzo</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        name="breakTimeStart" 
                        label={<span style={{ color: '#f49b33' }}>Inicio descanso</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="breakTimeEnd" 
                        label={<span style={{ color: '#f49b33' }}>Fin descanso</span>}
                      >
                        <TimePicker 
                          format="HH:mm" 
                          style={{ 
                            backgroundColor: '#2a2a2a',
                            color: '#fff',
                            width: '100%'
                          }}
                          popupStyle={{
                            backgroundColor: '#353435'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item 
                    name="workDays" 
                    label={<span style={{ color: '#f49b33' }}>Días laborales</span>}
                  >
                    <Checkbox.Group
                      options={[
                        { label: <span style={{ color: '#fff' }}>Lunes</span>, value: '1' },
                        { label: <span style={{ color: '#fff' }}>Martes</span>, value: '2' },
                        { label: <span style={{ color: '#fff' }}>Miércoles</span>, value: '3' },
                        { label: <span style={{ color: '#fff' }}>Jueves</span>, value: '4' },
                        { label: <span style={{ color: '#fff' }}>Viernes</span>, value: '5' },
                        { label: <span style={{ color: '#fff' }}>Sábado</span>, value: '6' },
                        { label: <span style={{ color: '#fff' }}>Domingo</span>, value: '7' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        )}

      </div>
    </div>
  );

};

export default UserData;